import type { MatchState } from '../match/MatchState';
import type { PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { clamp, distance, lerp, type Vector2 } from '../match/Vector2';
import { ANCHOR_PROFILES } from './AnchorPolicy';
import type { FormationRelationship } from './FormationRelationships';
import type { PlayerIntentProposal } from './PlayerIntent';
import { roleFamily, roleProfile } from './RoleProfiles';
import type { TeamAIState } from './TeamAIDirector';
import { TacticalRoleSystem } from './TacticalRoleSystem';

const relativeProgress = (player: PlayerRuntimeState, x: number) => PITCH_GEOMETRY.relativeProgress(player.teamSide, x);
const worldX = (side: 0 | 1, progress: number) => PITCH_GEOMETRY.worldX(side, progress);

export class OffensiveCoordinator {
  private readonly tacticalRoles = new TacticalRoleSystem();

  createIntents(
    state: MatchState,
    owner: PlayerRuntimeState,
    plan: TeamAIState,
    relationships: readonly FormationRelationship[],
  ): Map<string, PlayerIntentProposal> {
    const intents = new Map<string, PlayerIntentProposal>();
    const teammates = state.players.filter((player) => player.teamSide === owner.teamSide
      && player.playerId !== owner.playerId && player.positionType !== 'GK' && !this.isReturningToShape(player, state));
    const overlapIds = new Set(relationships.filter((item) => item.type === 'OVERLAP').map((item) => item.fromPlayerId));
    const runnerCandidates = teammates
      // Midfielders already have their own arrival/support mechanism below.
      // Letting them consume the regular runner budget in ordinary possession
      // turns a useful passing option into a second striker and is precisely
      // what leaves the ball carrier with no nearby support. In a true
      // transition they may still join the forward wave.
      .filter((player) => player.positionType === 'FW' || overlapIds.has(player.playerId)
        || (plan.phase === 'ATTACKING_TRANSITION' && player.positionType === 'MF'))
      .sort((a, b) => this.runPriority(b, plan, overlapIds.has(b.playerId))
        - this.runPriority(a, plan, overlapIds.has(a.playerId)) || a.playerId.localeCompare(b.playerId));
    const runners = runnerCandidates.slice(0, plan.runnerCount);
    // Preserve at least one wide threat whenever the formation offers one.
    // Without it, the highest-priority strikers and central midfielders take
    // every run budget and the two touchlines become decorative only.
    const wideRunner = runnerCandidates
      .filter((player) => this.isWideOutlet(player))
      .sort((a, b) => Math.abs(b.homePosition.y - PITCH_GEOMETRY.centre.y) - Math.abs(a.homePosition.y - PITCH_GEOMETRY.centre.y)
        || this.runPriority(b, plan, overlapIds.has(b.playerId)) - this.runPriority(a, plan, overlapIds.has(a.playerId))
        || a.playerId.localeCompare(b.playerId))[0];
    if (wideRunner && plan.runnerCount >= 2 && !runners.some((player) => player.playerId === wideRunner.playerId)) {
      runners[runners.length - 1] = wideRunner;
    }
    const runnerIds = new Set(runners.map((player) => player.playerId));

    runners.forEach((runner, index) => intents.set(runner.playerId, {
      movementState: 'MAKING_RUN',
      reason: 'ATTACK_SPACE',
      idealTarget: this.runTarget(owner, runner, index, plan, state.config.maximumForwardRunProgress),
      durationSeconds: state.config.intentMaximumDurationSeconds,
      bypassAnchor: true,
    }));
    // A midfielder's arrival is intentionally independent from the scarce
    // forward-run budget. Otherwise strikers win that budget every time and
    // midfield becomes a stationary chain of safe passes behind the ball.
    const midfieldArrivals = this.midfieldArrivalIntents(state, owner, teammates, runnerIds, plan);
    const recycleSupport = this.recycleSupportIntents(state, owner, teammates);
    for (const teammate of teammates) {
      if (runnerIds.has(teammate.playerId) || midfieldArrivals.has(teammate.playerId) || recycleSupport.has(teammate.playerId)) continue;
      const explicitlySupportsOwner = relationships.some((item) => item.type === 'SUPPORT'
        && item.fromPlayerId === teammate.playerId && item.toPlayerId === owner.playerId);
      intents.set(teammate.playerId, {
        movementState: 'SUPPORTING_ATTACK',
        reason: 'SUPPORT_POSSESSION',
        idealTarget: this.supportTarget(owner, teammate, plan, explicitlySupportsOwner),
      });
    }
    for (const [playerId, proposal] of midfieldArrivals) intents.set(playerId, proposal);
    for (const [playerId, proposal] of recycleSupport) intents.set(playerId, proposal);
    return intents;
  }

  /**
   * Gives the possession side a genuine midfield line: one player arrives in
   * front of the ball to combine, while the remaining midfielders retain a
   * safer passing angle. The advancing player is chosen by role and space,
   * not by a race against the forwards for the team's runner budget.
   */
  private midfieldArrivalIntents(
    state: MatchState,
    owner: PlayerRuntimeState,
    teammates: readonly PlayerRuntimeState[],
    runnerIds: ReadonlySet<string>,
    plan: TeamAIState,
  ): Map<string, PlayerIntentProposal> {
    if (!['PROGRESSION', 'FINAL_THIRD', 'ATTACKING_TRANSITION'].includes(plan.phase)) return new Map();
    const ownerProgress = relativeProgress(owner, owner.position.x);
    const candidates = teammates
      .filter((player) => player.positionType === 'MF' && !runnerIds.has(player.playerId))
      .sort((first, second) => this.midfieldArrivalPriority(second, plan) - this.midfieldArrivalPriority(first, plan)
        || distance(first.position, owner.position) - distance(second.position, owner.position)
        || first.playerId.localeCompare(second.playerId));
    const count = plan.phase === 'ATTACKING_TRANSITION' || (plan.phase === 'FINAL_THIRD' && plan.mentality >= .62) ? 2 : 1;
    const defenders = state.players.filter((player) => player.teamSide !== owner.teamSide && player.positionType !== 'GK');
    const usedLanes: number[] = [];

    return new Map(candidates.slice(0, count).map((player, index) => {
      const profile = roleProfile(player.role, player.positionType);
      const playerProgress = relativeProgress(player, player.position.x);
      const homeProgress = relativeProgress(player, player.homePosition.x);
      const phaseBurst = plan.phase === 'ATTACKING_TRANSITION' ? 12 : plan.phase === 'FINAL_THIRD' ? 9 : 7;
      const roleBurst = profile.advanceBias * .45 + (player.stats.speed - 50) * .025;
      const ceiling = homeProgress + (roleFamily(player.role, player.positionType) === 'HOLDING_MIDFIELDER' ? 16 : 25 + profile.advanceBias);
      const progress = clamp(
        Math.max(playerProgress + 4, ownerProgress + phaseBurst + roleBurst - index * 3),
        Math.max(8, homeProgress - 8),
        Math.min(92, ceiling),
      );
      const target = this.openArrivalLane(owner, player, progress, defenders, usedLanes, index);
      usedLanes.push(target.y);
      return [player.playerId, {
        movementState: 'SUPPORTING_ATTACK',
        reason: 'MIDFIELD_ARRIVAL',
        idealTarget: target,
        bypassAnchor: true,
      }];
    }));
  }

  private midfieldArrivalPriority(player: PlayerRuntimeState, plan: TeamAIState): number {
    const profile = roleProfile(player.role, player.positionType);
    const family = roleFamily(player.role, player.positionType);
    const familyBonus = family === 'ATTACKING_MIDFIELDER' ? 22 : family === 'CENTRAL_MIDFIELDER' ? 12 : 0;
    return familyBonus + profile.runPriority + profile.advanceBias * 2 + player.stats.control * .18 + player.stats.speed * .12
      + (this.tacticalRoles.movementBias(player.tacticalProfile.assignedRole, 'forwardRunBias') - 1) * 40
      + plan.risk * profile.advanceBias;
  }

  private openArrivalLane(
    owner: PlayerRuntimeState,
    player: PlayerRuntimeState,
    progress: number,
    defenders: readonly PlayerRuntimeState[],
    usedLanes: readonly number[],
    index: number,
  ): Vector2 {
    const side = index % 2 === 0 ? (owner.position.y < 50 ? 1 : -1) : (owner.position.y < 50 ? -1 : 1);
    const options = [
      player.homePosition.y,
      owner.position.y + side * 16,
      owner.position.y - side * 22,
      lerp(player.homePosition.y, owner.position.y, .45) + side * 9,
    ].map((y) => clamp(y, 8, 92));
    const targetX = worldX(owner.teamSide, progress);
    const targetY = [...new Set(options)].sort((first, second) => {
      const space = (lane: number) => defenders.length
        ? Math.min(...defenders.map((defender) => distance(defender.position, { x: targetX, y: lane }))) : 30;
      const separation = (lane: number) => usedLanes.length ? Math.min(...usedLanes.map((used) => Math.abs(lane - used))) * .5 : 0;
      return space(second) + separation(second) - space(first) - separation(first)
        || Math.abs(second - player.homePosition.y) - Math.abs(first - player.homePosition.y);
    })[0];
    return { x: targetX, y: targetY };
  }

  /**
   * When a forward is boxed in, midfield must offer a deliberate reset rather
   * than freezing in its generic support anchors. These targets sit behind and
   * to either side of the ball, giving the holder a safe backward pass that
   * can draw the defensive block out before the next attack.
   */
  private recycleSupportIntents(
    state: MatchState,
    owner: PlayerRuntimeState,
    teammates: readonly PlayerRuntimeState[],
  ): Map<string, PlayerIntentProposal> {
    const ownerProgress = relativeProgress(owner, owner.position.x);
    const nearbyDefenders = state.players.filter((player) => player.teamSide !== owner.teamSide
      && player.positionType !== 'GK' && distance(player.position, owner.position) <= 8.5);
    if (owner.positionType !== 'FW' || ownerProgress < PITCH_GEOMETRY.fieldThirds.finalStart || nearbyDefenders.length < 2) {
      return new Map();
    }
    const candidates = teammates
      .filter((player) => player.positionType === 'MF')
      .sort((first, second) => distance(first.position, owner.position) - distance(second.position, owner.position)
        || first.playerId.localeCompare(second.playerId))
      .slice(0, 2);
    const usedLanes: number[] = [];
    return new Map(candidates.map((player, index) => {
      const progress = clamp(ownerProgress - 11 - index * 5, 48, 79);
      const target = this.openRecycleLane(owner, player, progress, nearbyDefenders, usedLanes);
      usedLanes.push(target.y);
      return [player.playerId, {
        movementState: 'SUPPORTING_ATTACK',
        reason: 'RECYCLE_ATTACK',
        idealTarget: target,
        durationSeconds: Math.min(4, state.config.intentMaximumDurationSeconds),
      }];
    }));
  }

  private openRecycleLane(
    owner: PlayerRuntimeState,
    player: PlayerRuntimeState,
    progress: number,
    defenders: readonly PlayerRuntimeState[],
    usedLanes: readonly number[],
  ): Vector2 {
    const options = [owner.position.y - 17, owner.position.y + 17, player.homePosition.y, owner.position.y - 29, owner.position.y + 29]
      .map((y) => clamp(y, 8, 92));
    const targetX = worldX(owner.teamSide, progress);
    const targetY = [...new Set(options)].sort((first, second) => {
      const space = (lane: number) => Math.min(...defenders.map((defender) => distance(defender.position, { x: targetX, y: lane })));
      const separation = (lane: number) => usedLanes.length ? Math.min(...usedLanes.map((used) => Math.abs(lane - used))) * .45 : 0;
      return space(second) + separation(second) - space(first) - separation(first)
        || Math.abs(second - player.homePosition.y) - Math.abs(first - player.homePosition.y);
    })[0];
    return { x: targetX, y: targetY };
  }

  private runTarget(
    owner: PlayerRuntimeState,
    runner: PlayerRuntimeState,
    index: number,
    plan: TeamAIState,
    maximumForwardRunProgress: number,
  ): Vector2 {
    const ownerProgress = relativeProgress(owner, owner.position.x);
    const runnerProgress = relativeProgress(runner, runner.position.x);
    const burst = (plan.phase === 'ATTACKING_TRANSITION' ? 14 : 9)
      + (plan.tempo - 0.5) * 2 + (plan.risk - 0.5) * 2
      + (runner.stats.speed - 50) * 0.035;
    const targetProgress = clamp(
      Math.max(runnerProgress + 5, ownerProgress + burst),
      8,
      runner.positionType === 'FW' ? maximumForwardRunProgress : 96,
    );
    const wideRunner = this.isWideOutlet(runner);
    const lateralRun = (5 + (plan.width - 0.5) * 2) * (index % 2 === 0 ? -1 : 1);
    return {
      x: worldX(runner.teamSide, targetProgress),
      // Wide players stretch the back line and offer an actual channel. A
      // central runner may bend towards the holder, but a wide one keeps the
      // lane they were selected to attack.
      y: wideRunner
        ? clamp(lerp(runner.homePosition.y, owner.position.y, 0.05), 7, 93)
        : clamp(lerp(runner.homePosition.y, owner.position.y, 0.14) + lateralRun, 7, 93),
    };
  }

  private supportTarget(
    owner: PlayerRuntimeState,
    teammate: PlayerRuntimeState,
    plan: TeamAIState,
    explicitlySupportsOwner: boolean,
  ): Vector2 {
    const ownerProgress = relativeProgress(owner, owner.position.x);
    const homeProgress = relativeProgress(teammate, teammate.homePosition.x);
    const profile = roleProfile(teammate.role, teammate.positionType);
    const baseDepth = teammate.positionType === 'DF' ? 18 : 10;
    const depth = Math.max(5, baseDepth - (plan.tempo - 0.5) * 4 - (explicitlySupportsOwner ? 1 : 0));
    const roleRange = teammate.positionType === 'DF'
      ? { drop: 8, advance: 15 + (plan.risk - 0.5) * profile.advanceBias, lateral: 0.1 + (plan.width - 0.5) * 0.08 }
      : teammate.positionType === 'MF'
        ? { drop: 17, advance: 23 + (plan.risk - 0.5) * profile.advanceBias, lateral: 0.26 + (plan.width - 0.5) * 0.16 }
        : { drop: 14, advance: 20 + (plan.risk - 0.5) * profile.advanceBias, lateral: 0.34 + (plan.width - 0.5) * 0.18 };
    const wideProgressionSupport = this.isWideOutlet(teammate)
      && teammate.positionType !== 'DF'
      && ['PROGRESSION', 'FINAL_THIRD', 'ATTACKING_TRANSITION'].includes(plan.phase);
    const supportProgress = clamp(
      clamp(wideProgressionSupport ? ownerProgress + 6 : ownerProgress - depth, 5, 90),
      Math.max(4, homeProgress - roleRange.drop),
      Math.min(96, homeProgress + roleRange.advance),
    );
    const lateralTarget = wideProgressionSupport
      ? lerp(teammate.homePosition.y, owner.position.y, .08)
      : lerp(teammate.homePosition.y, owner.position.y, roleRange.lateral);
    const urgency = plan.phase === 'ATTACKING_TRANSITION' ? 0.7 : 0.45 + (plan.tempo - 0.5) * 0.1;
    return {
      x: worldX(teammate.teamSide, lerp(homeProgress, supportProgress, urgency)),
      y: lateralTarget,
    };
  }

  private runPriority(player: PlayerRuntimeState, plan: TeamAIState, overlap: boolean): number {
    const profile = roleProfile(player.role, player.positionType);
    return profile.runPriority + player.stats.speed * 0.45 + player.stats.kick * 0.35 + player.stats.control * 0.2
      + plan.risk * profile.advanceBias + (overlap && plan.width >= 0.5 ? 12 : 0)
      + (this.tacticalRoles.movementBias(player.tacticalProfile.assignedRole, 'forwardRunBias') - 1) * 40;
  }

  private isWideOutlet(player: PlayerRuntimeState): boolean {
    const family = roleFamily(player.role, player.positionType);
    return family === 'WINGER' || family === 'FULL_BACK' || family === 'WING_BACK'
      || Math.abs(player.homePosition.y - PITCH_GEOMETRY.centre.y) >= 24;
  }

  private isReturningToShape(player: PlayerRuntimeState, state: MatchState): boolean {
    if (player.intent.reason !== 'RETURN_TO_SHAPE') return false;
    const minimumReturnElapsed = player.intent.expiresAt === undefined || state.time >= player.intent.expiresAt;
    const reachedAnchor = distance(player.position, player.anchorPosition) <= ANCHOR_PROFILES[player.positionType].returnRadius;
    return !minimumReturnElapsed || !reachedAnchor;
  }
}
